import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Footer from "../Footer/Footer";
import {
  getSavedDataFromLocalDB,
  getStoreCollections,
  getCollectionsForStore,
} from "../../utils/storeLocalDB";
import { url } from "../../Constant/TextConstant";
import { useNavigate } from "react-router-dom";
import { encryptData } from "../../utils/encryption";
import Header from "../Header";
import { Helmet } from "react-helmet";
import { useUserLocation } from "../../utils/useUserLocation"; // Import the hook

// Styled components (remain the same)
const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
`;

const ContentContainer = styled.main`
  flex: 1;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Title = styled.h2`
  font-size: 2rem;
  color: #2c3e50;
  margin-bottom: 2rem;
  text-align: center;
  position: relative;
  padding-bottom: 0.5rem;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: #3498db;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const StoresList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const StoreCard = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const StoreImage = styled.div`
  height: 180px;
  background-color: #ecf0f1;
  background-image: ${(props) =>
    props.imageUrl ? `url(${props.imageUrl})` : "none"};
  background-size: cover;
  background-position: center;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.1),
      rgba(0, 0, 0, 0.3)
    );
  }
`;

const StoreDetails = styled.div`
  padding: 1.5rem;
`;

const StoreName = styled.h3`
  font-size: 1.25rem;
  color: #2c3e50;
  margin-bottom: 0.5rem;
`;

const StoreAddress = styled.p`
  color: #7f8c8d;
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const StoreContact = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  color: #3498db;
  font-size: 0.9rem;
`;

const CollectionTag = styled.span`
  display: inline-block;
  background-color: #e8f4fc;
  color: #3498db;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const EmptyStateText = styled.p`
  font-size: 1.1rem;
  color: #7f8c8d;
  margin-bottom: 1.5rem;
`;

const ExploreButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 5px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #2980b9;
  }
`;

const CollectionSelector = styled.div`
  margin-bottom: 2rem;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const CollectionButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: ${(props) => (props.$active ? "#3498db" : "#ecf0f1")};
  color: ${(props) => (props.$active ? "white" : "#2c3e50")};
  border: none;
  border-radius: 20px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${(props) => (props.$active ? "#2980b9" : "#dfe6e9")};
  }
`;

function SavedStoresPage() {
  const [savedStores, setSavedStores] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState("all");
  const navigate = useNavigate();
 useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Use the useUserLocation hook
  const {
    location: currentLocation,
    error: locationError,
    isLoading: isLocationLoading,
    updateLocation,
  } = useUserLocation();

  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return localStorage.getItem("selectedCategory") || "products";
  });

  // State to hold the user location data
  const [userLocation, setUserLocation] = useState({
    coordinates: { latitude: 0, longitude: 0 },
    address: "",
    details: null,
  });

  // Sync location from hook to component state
  useEffect(() => {
    if (currentLocation) {
      setUserLocation({
        coordinates: {
          latitude: currentLocation.coordinates.latitude,
          longitude: currentLocation.coordinates.longitude,
        },
        address: currentLocation.address || "",
        details: currentLocation.details || null,
      });
    }
  }, [currentLocation]);

  // Update localStorage whenever selectedCategory changes
  useEffect(() => {
    localStorage.setItem("selectedCategory", selectedCategory);
  }, [selectedCategory]);

  // Handler for modal close with location update
  const handleModalClose = (shouldClose, newCoordinates = null) => {
    setShowModal(false);
    if (newCoordinates) {
      // Update location through the hook's update function
      updateLocation({
        coordinates: {
          latitude: newCoordinates.latitude,
          longitude: newCoordinates.longitude,
        },
        address: newCoordinates.address || "",
        details: newCoordinates.details || null,
      });
    }
  };

  const handleStoreClick = (storeMobile) => {
    navigate(`/store-detail/${encryptData(storeMobile)}`);
  };

  useEffect(() => {
    const loadStores = () => {
      try {
        const savedData = getSavedDataFromLocalDB();
        const storeCollections = savedData.storeCollections || {};

        // Get all collection names
        const collectionNames = Object.keys(storeCollections);
        setCollections(collectionNames);

        // Get all stores from all collections (or selected collection)
        let stores = [];
        if (selectedCollection === "all") {
          // Combine stores from all collections, removing duplicates
          const storeMap = new Map();
          collectionNames.forEach((collectionName) => {
            storeCollections[collectionName].stores.forEach((store) => {
              if (!storeMap.has(store.storeMobile)) {
                storeMap.set(store.storeMobile, {
                  ...store,
                  collections: getCollectionsForStore(store.storeMobile),
                });
              }
            });
          });
          stores = Array.from(storeMap.values());
        } else if (storeCollections[selectedCollection]) {
          stores = storeCollections[selectedCollection].stores.map((store) => ({
            ...store,
            collections: [selectedCollection],
          }));
        }

        setSavedStores(stores);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    loadStores();
  }, [selectedCollection]);

  if (isLoading) {
    return (
      <PageContainer>
        <ContentContainer>
          <Title>Your Saved Stores</Title>
          <p>Loading...</p>
        </ContentContainer>
        <Footer />
      </PageContainer>
    );
  }

  return (
    <>
      <Helmet>
        <title>Your Saved Stores | My Collections</title>
        <meta
          name="description"
          content={`Browse your saved stores and collections. You have ${
            savedStores.length
          } ${savedStores.length === 1 ? "store" : "stores"} saved across ${
            collections.length
          } collections.`}
        />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Your Saved Stores | My Collections"
        />
        <meta
          property="og:description"
          content="View and manage your saved stores and collections"
        />
        <meta property="og:image" content="/images/saved-stores-preview.jpg" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Your Saved Stores | My Collections"
        />
        <meta
          name="twitter:description"
          content="Access your saved stores and collections anytime"
        />
        <meta name="twitter:image" content="/images/saved-stores-preview.jpg" />
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Saved Stores",
            description: "User's collection of saved stores",
            url: window.location.href,
            hasPart: savedStores.map((store) => ({
              "@type": "Store",
              name: store.storeName,
              url: `${window.location.origin}/store-detail/${encryptData(
                store.storeMobile
              )}`,
            })),
          })}
        </script>
      </Helmet>
      <Header
        hasShowOptions={true}
        userLocation={userLocation}
        setShowModal={setShowModal}
        setUserLocation={updateLocation} // Use the hook's update function
        showModal={showModal}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />
      <PageContainer>
        <ContentContainer>
          <Title>Your Saved Stores</Title>

          {collections.length > 0 && (
            <CollectionSelector>
              <CollectionButton
                $active={selectedCollection === "all"}
                onClick={() => setSelectedCollection("all")}
              >
                All Stores
              </CollectionButton>
              {collections.map((collection) => (
                <CollectionButton
                  key={collection}
                  $active={selectedCollection === collection}
                  onClick={() => setSelectedCollection(collection)}
                >
                  {collection}
                </CollectionButton>
              ))}
            </CollectionSelector>
          )}

          {savedStores.length > 0 ? (
            <StoresList>
              {savedStores.map((store) => (
                <StoreCard
                  key={store.storeMobile}
                  onClick={() => handleStoreClick(store.storeMobile)}
                >
                  <StoreImage
                    imageUrl={
                      store.shopkeeperImage
                        ? `${url.productImageStartingPath}/${store.shopkeeperImage}`
                        : "/assets/images/default-store.png"
                    }
                  />
                  <StoreDetails>
                    <StoreName>{store.storeName}</StoreName>
                    <StoreAddress>{store.address}</StoreAddress>
                    {selectedCollection === "all" && store.collections && (
                      <div>
                        {store.collections.map((collection) => (
                          <CollectionTag key={collection}>
                            {collection}
                          </CollectionTag>
                        ))}
                      </div>
                    )}
                    <StoreContact>
                      <span>📞 {store.storeMobile}</span>
                    </StoreContact>
                    <StoreContact>
                      <span>⭐ {store.rating?.toFixed(1) || "0.0"}</span>
                    </StoreContact>
                  </StoreDetails>
                </StoreCard>
              ))}
            </StoresList>
          ) : (
            <EmptyState>
              <EmptyStateText>
                {selectedCollection === "all"
                  ? "You haven't saved any stores yet"
                  : `No stores saved in "${selectedCollection}" collection`}
              </EmptyStateText>
              <ExploreButton onClick={() => navigate("/explore")}>
                Explore Nearby Stores
              </ExploreButton>
            </EmptyState>
          )}
        </ContentContainer>
        <Footer />
      </PageContainer>
    </>
  );
}

export default SavedStoresPage;
